import {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  type KeyboardEvent,
  type ChangeEvent,
} from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { ActionTypes } from "@/actions"
import { useAuth } from "../auth/AuthContext"
import {
  searchAll,
  SEARCH_PER_COLLECTION_LIMIT,
  type SearchCollectionResult,
  type SearchResultEntry,
} from "../api/searchApi"
import { fetchMyFormSubmissions, type MyFormSubmissionEntry } from "../api/publicFormsApi"
import { fetchFormConfigsWithMeta } from "../api/formConfigApi"
import { listInboxItems } from "../api/inboxApi"
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs"
import { buildCollectionPath, normalizeOrgSegment, parseCollectionTablePath } from "../utils/collectionPaths"
import { isOrganizationScopingDisabled } from "../utils/organizationScoping"
import { buildCollectionSearchNavigationState } from "./searchNavigationState"
import { confirmLeaveItemEditorIfNeeded } from "../component/itemEditorNavigationGuard"
import { usePermissions, usePermission } from "../acl/usePermission"
import { reloadAppConfig } from "../acl/reloadAppConfig"
import type { PermissionCheckRequest } from "../api/aclApi"
import type { ComponentWithSource } from "../api/componentsData"
import {
  USER_COLLECTION_SLUG,
  SHOW_FILES_MENU,
} from "./menuItemsConstants"
import { toast } from "react-toastify"

export type MenuSearchHit = {
  id: string
  title: string
  subtitle?: string
  onNavigate: () => void
}

export type SearchPanelRow =
  | {
    type: "menu"
    key: string
    title: string
    subtitle?: string
    onActivate: () => void
  }
  | {
    type: "collection"
    key: string
    component: ComponentWithSource
  }
  | {
    type: "item"
    key: string
    col: SearchCollectionResult
    entry: SearchResultEntry
  }

function buildMenuSearchHits(
  termRaw: string,
  ctx: {
    isAdmin: boolean
    hasFormConfigAccess: boolean
    isPendingSetup: boolean
    showComponentsNav: boolean
    hasCollections: boolean
    collectionRouteContext: ReturnType<typeof parseCollectionTablePath>
    navCollectionLimits: () => void
    navAdminComponents: () => void
    navMyCollections: () => void
    navSubmissions: () => void
    navInbox: () => void
    navFiles: () => void
    navActions: () => void
    hideInbox: boolean
    hideActions: boolean
    hideSubmissions: boolean
    navAdminPermissions: () => void
    navAdminForms: () => void
    navAdminJsonFeeds: () => void
    navAdminUsers: () => void
    navAdminGroups: () => void
    navAdminTenants: () => void
    navAdminEmail: () => void
    navAdminEmailLogs: () => void
    navAdminEmailProvider: () => void
    navAdminEmailTest: () => void
    navAdminMongo: () => void
    navAdminSystem: () => void
    navAdminConnectors: () => void
    handleReloadData: () => void
  }
): MenuSearchHit[] {
  const term = termRaw.trim().toLowerCase()
  if (!term || !ctx.showComponentsNav || ctx.isPendingSetup) {
    return []
  }

  const hits: MenuSearchHit[] = []

  const add = (
    id: string,
    title: string,
    subtitle: string | undefined,
    keywords: string[],
    onNavigate: () => void
  ) => {
    const blob = [title, subtitle, ...keywords].join(" ").toLowerCase()
    if (!blob.includes(term)) {
      return
    }
    hits.push({ id, title, subtitle, onNavigate })
  }

  if (!ctx.isPendingSetup) {
    const goCollectionManager = ctx.isAdmin ? ctx.navAdminComponents : ctx.navMyCollections
    add(
      "collection-manager",
      "Collection manager",
      "Collections you created",
      ["wrench", "my collections", "tables"],
      goCollectionManager
    )
  }

  if (!ctx.hideInbox) {
    add(
      "inbox",
      "Inbox",
      "Captures waiting to be filed",
      ["messages", "quick add"],
      ctx.navInbox
    )
  }

  if (!ctx.hideSubmissions) {
    add(
      "submissions",
      "Submissions",
      "Your form submissions",
      ["dashboard", "home", "forms", "recent"],
      ctx.navSubmissions
    )
  }

  if (ctx.hasCollections && !ctx.hideActions) {
    add(
      "actions",
      "Actions",
      "Automations per component",
      ["automations", "workflows", "bulk"],
      ctx.navActions
    )
  }

  if (SHOW_FILES_MENU && ctx.showComponentsNav) {
    add(
      "files",
      "Files",
      "Uploaded files and links",
      ["uploads", "documents", "attachments"],
      ctx.navFiles
    )
  }

  if (ctx.collectionRouteContext) {
    add(
      "reload-data",
      "Reload data",
      "Current table",
      ["refresh", "reload", "table"],
      ctx.handleReloadData
    )
  }

  if (!ctx.isAdmin && ctx.hasFormConfigAccess) {
    add(
      "public-forms",
      "Public Forms",
      "Form configuration",
      ["forms", "surveys", "published"],
      ctx.navAdminForms
    )
  }

  if (ctx.isAdmin) {
    add(
      "collection-limits",
      "Collection limits",
      undefined,
      ["limits", "quotas", "setup"],
      ctx.navCollectionLimits
    )
    add(
      "manage-collections",
      "Manage collections",
      "Administrator",
      ["admin", "components", "tables"],
      ctx.navAdminComponents
    )
    add(
      "connections",
      "Connections",
      "External server connectors",
      ["connectors", "integrations", "servers", "remote", "cms"],
      ctx.navAdminConnectors
    )
    add(
      "permissions",
      "Permissions",
      "Access control",
      ["acl", "access", "roles"],
      ctx.navAdminPermissions
    )
    add(
      "public-forms",
      "Public Forms",
      undefined,
      ["forms", "surveys", "published"],
      ctx.navAdminForms
    )
    add(
      "json-feeds",
      "Public JSON feeds",
      undefined,
      ["json", "api", "feeds", "export"],
      ctx.navAdminJsonFeeds
    )
    add("users", "Users", undefined, ["accounts", "people", "directory"], ctx.navAdminUsers)
    add("groups", "Groups", undefined, ["teams", "membership", "access groups"], ctx.navAdminGroups)
    add(
      "organizations",
      "Organizations",
      undefined,
      ["tenants", "orgs", "org"],
      ctx.navAdminTenants
    )
    add(
      "email",
      "Email",
      "Logs, provider settings, test mail",
      ["smtp", "mail", "email", "delivery", "resend", "mailjet"],
      ctx.navAdminEmail
    )
    add(
      "email-logs",
      "Email logs",
      undefined,
      ["logging", "audit", "activity", "delivery"],
      ctx.navAdminEmailLogs
    )
    add(
      "email-provider",
      "Email provider",
      "Mailjet or Resend",
      ["provider", "settings", "resend", "mailjet", "smtp"],
      ctx.navAdminEmailProvider
    )
    add(
      "email-test",
      "Test mail",
      undefined,
      ["smtp", "mail", "email", "test", "send"],
      ctx.navAdminEmailTest
    )
    add(
      "mongo-raw",
      "Mongo (raw)",
      "Browse database collections",
      ["mongodb", "database", "documents", "json"],
      ctx.navAdminMongo
    )
    add(
      "system",
      "System",
      "Maintenance and demo account",
      ["settings", "maintenance", "demo", "system"],
      ctx.navAdminSystem
    )
  }

  return hits
}

export type MenuItemsModel = ReturnType<typeof useMenuItemsModel>

export function useMenuItemsModel(onNav: () => void) {
  const [isSubUsersOpen, setIsSubUsersOpen] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [itemCollections, setItemCollections] = useState<SearchCollectionResult[] | null>(null)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemsError, setItemsError] = useState<string | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const [itemSelectedIndex, setItemSelectedIndex] = useState<number>(-1)
  const [collectionSelectedIndex, setCollectionSelectedIndex] = useState<number>(-1)
  const menuButtonRefs = useRef<HTMLButtonElement[]>([])

  const dispatch = useDispatch()
  const reduxUser = useSelector((state: any) => state.user)
  const { user: authUser, refreshUser } = useAuth()
  const location = useLocation()
  const pathname = location.pathname
  const debugMode = useSelector((state: any) => state.ui?.debugMode) || false
  const reloadConfigNonce = useSelector((state: any) => state.ui?.reloadConfigNonce ?? 0)

  const user = authUser || reduxUser
  const hideInbox = user?.uiSettings?.hideInbox === true
  const hideActions = user?.uiSettings?.hideActions === true
  const hideSubmissions = user?.uiSettings?.hideSubmissions === true
  const components = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY)
  const componentsLoading = useSelector((state: any) => state.components.loading) || false
  const componentsError = useSelector((state: any) => state.components.error) || null
  const tableMeta = useSelector((state: any) => state.table.meta ?? EMPTY_ARRAY)
  const hasCollections = components.length > 0
  const isAdmin = Boolean(user?.admin || user?.info?.admin)
  const [hasFormConfigAccess, setHasFormConfigAccess] = useState(false)
  const effectiveDebugMode = isAdmin ? debugMode : false
  const userId = useMemo(() => {
    return (
      user?.uid ||
      user?.info?.uid ||
      user?.info?._id ||
      user?.info?.id ||
      user?.id ||
      user?._id ||
      null
    )
  }, [user])
  const lastRequestedUserIdRef = useRef<string | null>(null)
  const [submissionMenuStatus, setSubmissionMenuStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  )
  const [submissionMenuItems, setSubmissionMenuItems] = useState<MyFormSubmissionEntry[]>([])
  const [inboxCount, setInboxCount] = useState(0)
  const collectionRouteContext = useMemo(
    () => parseCollectionTablePath(location.pathname),
    [location.pathname]
  )

  useEffect(() => {
    if (userId && lastRequestedUserIdRef.current !== userId) {
      lastRequestedUserIdRef.current = userId
      dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user: user } })
    }
  }, [userId, user, dispatch])

  useEffect(() => {
    if (!userId || hideSubmissions) {
      setSubmissionMenuItems([])
      setSubmissionMenuStatus("ready")
      return
    }
    let cancelled = false
    setSubmissionMenuStatus("loading")
    fetchMyFormSubmissions(200)
      .then((list) => {
        if (!cancelled) {
          setSubmissionMenuItems(list)
          setSubmissionMenuStatus("ready")
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubmissionMenuItems([])
          setSubmissionMenuStatus("error")
        }
      })
    return () => {
      cancelled = true
    }
  }, [userId, reloadConfigNonce, hideSubmissions])

  const refreshInboxCount = useCallback(async () => {
    if (!userId || hideInbox) {
      setInboxCount(0)
      return
    }
    try {
      const items = await listInboxItems()
      setInboxCount(items.length)
    } catch {
      setInboxCount(0)
    }
  }, [userId, hideInbox])

  useEffect(() => {
    void refreshInboxCount()
  }, [refreshInboxCount, pathname])

  useEffect(() => {
    const handler = () => {
      void refreshInboxCount()
    }
    window.addEventListener("inbox-updated", handler)
    return () => window.removeEventListener("inbox-updated", handler)
  }, [refreshInboxCount])

  useEffect(() => {
    const term = searchTerm.trim()

    setItemSelectedIndex(-1)
    setCollectionSelectedIndex(-1)

    if (!term) {
      setItemCollections(null)
      setItemsError(null)
      setItemsLoading(false)
      if (searchAbortRef.current) {
        searchAbortRef.current.abort()
        searchAbortRef.current = null
      }
      return
    }

    const controller = new AbortController()
    searchAbortRef.current = controller
    setItemsLoading(true)
    setItemsError(null)

    const timeoutId = window.setTimeout(() => {
      searchAll(term, SEARCH_PER_COLLECTION_LIMIT, controller.signal)
        .then((collections) => {
          setItemCollections(collections)
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return
          }
          const message =
            error instanceof Error && error.message ? error.message : "Failed to search items"
          setItemsError(message)
          setItemCollections(null)
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setItemsLoading(false)
          }
        })
    }, 350)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [searchTerm])

  const filteredComponents = useMemo(() => {
    if (!components || components.length === 0) {
      return []
    }
    const excludeUserCollection = (c: ComponentWithSource) =>
      (c.slug?.toLowerCase() || "") !== USER_COLLECTION_SLUG
    const bySearch =
      searchTerm.trim() === ""
        ? components
        : (() => {
          const term = searchTerm.toLowerCase()
          return components.filter(
            (c: ComponentWithSource) =>
              c.name?.toLowerCase().includes(term) ||
              c.slug?.toLowerCase().includes(term) ||
              c.dataSource?.toLowerCase().includes(term)
          )
        })()
    return bySearch.filter(excludeUserCollection)
  }, [components, searchTerm])

  const flattenedItems = useMemo(
    () =>
      itemCollections
        ? itemCollections.flatMap((col) => col.results.map((entry) => ({ col, entry })))
        : [],
    [itemCollections]
  )

  const navigate = useNavigate()

  const leaveItemEditorAndNavigate = useCallback(
    (...args: Parameters<typeof navigate>) => {
      if (!confirmLeaveItemEditorIfNeeded()) {
        return
      }
      navigate(...args)
      onNav()
    },
    [navigate, onNav]
  )

  const navAdminComponents = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/collections")
  }, [leaveItemEditorAndNavigate])

  const limit = user?.collectionLimit ?? user?.info?.collectionLimit
  const hasNoLimit = limit == null
  // Keep setup-gating only when user has no visible collections yet.
  // Users with ACL-granted collections must still see the Collections section.
  const isPendingSetup = !isAdmin && hasNoLimit && !hasCollections
  const collectionCount = user?.collectionCount ?? user?.info?.collectionCount ?? 0
  const atCollectionLimit = typeof limit === "number" && limit > 0 && collectionCount >= limit
  const canCreateCollectionByLimit = isAdmin || (!hasNoLimit && !atCollectionLimit)

  const createCollectionRequest = useMemo(
    () =>
      user
        ? { action: "create_collection" as const, targetType: "collection" as const, targetId: "*" }
        : null,
    [user]
  )
  const { allowed: canCreateCollection } = usePermission(createCollectionRequest, {
    enabled: Boolean(user),
  })

  const navMyCollections = useCallback(() => {
    leaveItemEditorAndNavigate("/components")
  }, [leaveItemEditorAndNavigate])

  const navCreateCollection = useCallback(() => {
    if (!user) {
      return
    }
    if (!canCreateCollection) {
      toast.warn("You do not have permission to create collections.", { autoClose: 6000 })
      return
    }
    if (!canCreateCollectionByLimit) {
      const message = hasNoLimit
        ? "You cannot create collections yet. Please wait for an administrator to give you access."
        : "You cannot create more collections. Please wait for an administrator to increase your limit or grant access."
      toast.warn(message, { autoClose: 8000 })
      return
    }
    if (isAdmin) {
      leaveItemEditorAndNavigate("/admin/collections", { state: { create: true } })
    } else {
      leaveItemEditorAndNavigate("/components", { state: { create: true } })
    }
  }, [
    user,
    canCreateCollection,
    canCreateCollectionByLimit,
    hasNoLimit,
    isAdmin,
    leaveItemEditorAndNavigate,
  ])

  const navSubmissions = useCallback(() => {
    leaveItemEditorAndNavigate("/")
  }, [leaveItemEditorAndNavigate])

  const navMySubmissionEntry = useCallback(
    (entry: MyFormSubmissionEntry) => {
      if (entry.archived) {
        alert(
          'This submission was archived. It stays in your list for reference but cannot be opened.'
        )
        return
      }
      leaveItemEditorAndNavigate(`/my-submissions/${entry.formToken}/${entry.uid}`)
    },
    [leaveItemEditorAndNavigate]
  )

  const navFiles = useCallback(() => {
    leaveItemEditorAndNavigate("/files")
  }, [leaveItemEditorAndNavigate])

  const navAdminPermissions = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/permissions")
  }, [leaveItemEditorAndNavigate])

  const navAdminForms = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/forms")
  }, [leaveItemEditorAndNavigate])

  const navAdminJsonFeeds = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/json-feeds")
  }, [leaveItemEditorAndNavigate])

  const navAdminUsers = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/users")
  }, [leaveItemEditorAndNavigate])

  const navAdminEmail = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/email")
  }, [leaveItemEditorAndNavigate])

  const navAdminEmailLogs = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/email/logs")
  }, [leaveItemEditorAndNavigate])

  const navAdminEmailProvider = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/email/provider")
  }, [leaveItemEditorAndNavigate])

  const navAdminGroups = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/groups")
  }, [leaveItemEditorAndNavigate])

  const navAdminEmailTest = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/email/test")
  }, [leaveItemEditorAndNavigate])

  const navAdminMongo = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/mongo")
  }, [leaveItemEditorAndNavigate])

  const navAdminSystem = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/system")
  }, [leaveItemEditorAndNavigate])

  const navAdminConnectors = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/connectors")
  }, [leaveItemEditorAndNavigate])

  const navCollectionLimits = useCallback(() => {
    leaveItemEditorAndNavigate("/")
  }, [leaveItemEditorAndNavigate])

  const navAdminTenants = useCallback(() => {
    leaveItemEditorAndNavigate("/admin/tenants")
  }, [leaveItemEditorAndNavigate])

  const navAdminActions = useCallback(() => {
    leaveItemEditorAndNavigate("/actions")
  }, [leaveItemEditorAndNavigate])

  const navActions = useCallback(() => {
    leaveItemEditorAndNavigate("/actions")
  }, [leaveItemEditorAndNavigate])

  const navInbox = useCallback(() => {
    leaveItemEditorAndNavigate("/inbox")
  }, [leaveItemEditorAndNavigate])

  useEffect(() => {
    if (!user || isAdmin) {
      setHasFormConfigAccess(isAdmin)
      return
    }

    let cancelled = false
    fetchFormConfigsWithMeta()
      .then((result) => {
        if (!cancelled) {
          setHasFormConfigAccess(result.meta?.formConfigAccess === true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasFormConfigAccess(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [user, isAdmin])

  useEffect(() => {
    if (!isAdmin && debugMode) {
      dispatch({ type: ActionTypes.UI_TOGGLE_DEBUG })
    }
  }, [isAdmin, debugMode, dispatch])

  const toggleDebugMode = useCallback(() => {
    if (!isAdmin) {
      return
    }
    dispatch({ type: ActionTypes.UI_TOGGLE_DEBUG })
  }, [isAdmin, dispatch])

  const navComponent = useCallback(
    (component: { slug?: string; organization?: string | null }) => {
      const sanitizedSlug =
        typeof component.slug === "string" ? component.slug.replace(/[!]+$/, "").trim() : ""
      if (!sanitizedSlug) return
      leaveItemEditorAndNavigate(
        buildCollectionPath({
          slug: sanitizedSlug,
          organization: isOrganizationScopingDisabled() ? undefined : component.organization,
        })
      )
    },
    [leaveItemEditorAndNavigate]
  )

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const clearSearch = useCallback(() => {
    setSearchTerm("")
  }, [])

  const handleRetryCollections = useCallback(() => {
    if (user) {
      dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user, force: true } })
    }
  }, [user, dispatch])

  const handleReloadData = useCallback(() => {
    if (!user) return

    refreshUser?.()
    reloadAppConfig(dispatch, user, {
      reloadCurrentCollection: Boolean(collectionRouteContext),
      pathname: pathname,
      tableMeta,
    })
  }, [user, refreshUser, collectionRouteContext, pathname, tableMeta, dispatch])

  const mongoCount = filteredComponents.filter(
    (c: ComponentWithSource) => c.dataSource === "mongodb"
  ).length

  const isLoading = componentsLoading
  const showComponentsNav = Boolean(user)

  const menuSearchHits = useMemo(
    () =>
      buildMenuSearchHits(searchTerm, {
        isAdmin,
        hasFormConfigAccess,
        isPendingSetup,
        showComponentsNav,
        hasCollections,
        collectionRouteContext,
        navCollectionLimits,
        navAdminComponents,
        navMyCollections,
        navSubmissions,
        navInbox,
        navFiles,
        navActions,
        hideInbox,
        hideActions,
        hideSubmissions,
        navAdminPermissions,
        navAdminForms,
        navAdminJsonFeeds,
        navAdminUsers,
        navAdminGroups,
        navAdminTenants,
        navAdminEmail,
        navAdminEmailLogs,
        navAdminEmailProvider,
        navAdminEmailTest,
        navAdminMongo,
        navAdminSystem,
        navAdminConnectors,
        handleReloadData,
      }),
    [
      searchTerm,
      isAdmin,
      hasFormConfigAccess,
      isPendingSetup,
      showComponentsNav,
      hasCollections,
      collectionRouteContext,
      navCollectionLimits,
      navAdminComponents,
      navMyCollections,
      navSubmissions,
      navInbox,
      navFiles,
      navActions,
      hideInbox,
      hideActions,
      hideSubmissions,
      navAdminPermissions,
      navAdminForms,
      navAdminJsonFeeds,
      navAdminUsers,
      navAdminGroups,
      navAdminTenants,
      navAdminEmail,
      navAdminEmailLogs,
      navAdminEmailProvider,
      navAdminEmailTest,
      navAdminMongo,
      navAdminSystem,
      navAdminConnectors,
      handleReloadData,
    ]
  )

  const searchPanelRows = useMemo((): SearchPanelRow[] => {
    const rows: SearchPanelRow[] = []
    let itemSeq = 0
    for (const h of menuSearchHits) {
      rows.push({
        type: "menu",
        key: `menu:${h.id}`,
        title: h.title,
        subtitle: h.subtitle,
        onActivate: h.onNavigate,
      })
    }
    if (filteredComponents.length === 1) {
      const c = filteredComponents[0]
      rows.push({
        type: "collection",
        key: `collection:${c.uid}`,
        component: c,
      })
    }
    for (const { col, entry } of flattenedItems) {
      rows.push({
        type: "item",
        key: `item:${itemSeq++}:${col.slug ?? col.collection}:${entry.ref ?? ""}`,
        col,
        entry,
      })
    }
    return rows
  }, [menuSearchHits, flattenedItems, filteredComponents])

  useEffect(() => {
    const term = searchTerm.trim()
    if (!term || filteredComponents.length !== 1) {
      return
    }
    const idx = searchPanelRows.findIndex((r) => r.type === "collection")
    if (idx >= 0) {
      setItemSelectedIndex(idx)
    }
  }, [searchTerm, searchPanelRows, filteredComponents.length])

  const collectionAclRequests = useMemo((): PermissionCheckRequest[] => {
    if (!user || filteredComponents.length === 0) return []
    const reqs: PermissionCheckRequest[] = []
    for (const c of filteredComponents) {
      const id = typeof c.uid === "string" ? c.uid.trim() : ""
      if (!id) continue
      reqs.push(
        { action: "list", targetType: "collection", targetId: id },
        { action: "view", targetType: "collection", targetId: id }
      )
    }
    return reqs
  }, [user, filteredComponents, reloadConfigNonce])

  const collectionAclEnabled = Boolean(user && !isLoading && collectionAclRequests.length > 0)

  const { results: collectionAclResults, loading: collectionAclLoading } = usePermissions(
    collectionAclRequests,
    { enabled: collectionAclEnabled }
  )

  const collectionNavAccessByUid = useMemo(() => {
    const map = new Map<string, { canList: boolean; canView: boolean }>()
    if (!user || collectionAclLoading) return map
    let r = 0
    for (const c of filteredComponents) {
      const id = typeof c.uid === "string" ? c.uid.trim() : ""
      if (!id) continue
      const listRes = collectionAclResults[r * 2]
      const viewRes = collectionAclResults[r * 2 + 1]
      map.set(id, {
        canList: listRes?.allowed ?? false,
        canView: viewRes?.allowed ?? false,
      })
      r++
    }
    return map
  }, [user, filteredComponents, collectionAclLoading, collectionAclResults])

  const isCollectionNavListButNoView = useCallback(
    (component: ComponentWithSource): boolean => {
      if (collectionAclLoading) return false
      const id = typeof component.uid === "string" ? component.uid.trim() : ""
      if (!id) return false
      const acc = collectionNavAccessByUid.get(id)
      if (!acc) return false
      return acc.canList && !acc.canView
    },
    [collectionAclLoading, collectionNavAccessByUid]
  )

  const onCollectionMenuNavigate = useCallback(
    (component: ComponentWithSource) => {
      if (isCollectionNavListButNoView(component)) {
        const label = component.name || component.slug || "this collection"
        alert(
          `You do not have permission to view the table for "${label}". Ask an administrator to grant view access.`
        )
        return
      }
      navComponent(component)
    },
    [isCollectionNavListButNoView, navComponent]
  )

  const resetMenuKeyboardNav = useCallback(() => {
    menuButtonRefs.current = []
  }, [])

  const registerMenuButton = useCallback((index: number) => (el: HTMLButtonElement | null) => {
    if (el) {
      menuButtonRefs.current[index] = el
    }
  }, [])

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (searchPanelRows.length > 0) {
        const maxIndex = searchPanelRows.length - 1
        if (e.key === "ArrowDown") {
          e.preventDefault()
          setItemSelectedIndex((prev: number) => {
            const base = prev < 0 ? 0 : prev
            return base >= maxIndex ? 0 : base + 1
          })
          return
        }
        if (e.key === "ArrowUp") {
          e.preventDefault()
          setItemSelectedIndex((prev: number) => {
            const base = prev < 0 ? 0 : prev
            return base <= 0 ? maxIndex : base - 1
          })
          return
        }
        if (e.key === "Enter") {
          e.preventDefault()
          const index = itemSelectedIndex >= 0 ? itemSelectedIndex : 0
          const row = searchPanelRows[index]
          if (!row) return
          if (row.type === "menu") {
            row.onActivate()
            return
          }
          if (row.type === "collection") {
            onCollectionMenuNavigate(row.component)
            return
          }
          const rawSlug =
            typeof (row.col.slug || row.col.collection) === "string"
              ? row.col.slug || row.col.collection
              : ""
          const slugValue = rawSlug.replace(/[!]+$/, "").trim()
          if (!slugValue) return
          const highlightUid = row.entry.ref || row.entry.item?.uid || null
          leaveItemEditorAndNavigate(buildCollectionPath({ slug: slugValue }), {
            state: buildCollectionSearchNavigationState({
              highlightUid,
              matchingField: row.entry.matchingField,
              matchingValue: row.entry.matchingValue,
              searchQuery: searchTerm,
            }),
          })
          return
        }
      } else if (menuButtonRefs.current.length > 0) {
        const maxIndex = menuButtonRefs.current.length - 1
        if (e.key === "ArrowDown") {
          e.preventDefault()
          setCollectionSelectedIndex((prev: number) => {
            const base = prev < 0 ? 0 : prev
            return base >= maxIndex ? 0 : base + 1
          })
          return
        }
        if (e.key === "ArrowUp") {
          e.preventDefault()
          setCollectionSelectedIndex((prev: number) => {
            const base = prev < 0 ? 0 : prev
            return base <= 0 ? maxIndex : base - 1
          })
          return
        }
        if (e.key === "Enter") {
          e.preventDefault()
          const index = collectionSelectedIndex >= 0 ? collectionSelectedIndex : 0
          const targetButton = menuButtonRefs.current[index]
          if (targetButton) {
            targetButton.click()
          }
        }
      }
    },
    [
      searchPanelRows,
      itemSelectedIndex,
      collectionSelectedIndex,
      searchTerm,
      leaveItemEditorAndNavigate,
      onCollectionMenuNavigate,
    ]
  )

  return {
    pathname,
    isSubUsersOpen,
    setIsSubUsersOpen,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    clearSearch,
    handleSearchKeyDown,
    itemsLoading,
    itemsError,
    itemSelectedIndex,
    collectionSelectedIndex,
    menuButtonRefs,
    resetMenuKeyboardNav,
    registerMenuButton,
    user,
    isAdmin,
    hasFormConfigAccess,
    components,
    componentsError,
    hasCollections,
    effectiveDebugMode,
    debugMode,
    toggleDebugMode,
    collectionRouteContext,
    filteredComponents,
    flattenedItems,
    menuSearchHits,
    searchPanelRows,
    isLoading,
    showComponentsNav,
    isPendingSetup,
    mongoCount,
    submissionMenuStatus,
    submissionMenuItems,
    navMySubmissionEntry,
    canCreateCollection,
    navAdminComponents,
    navMyCollections,
    navCreateCollection,
    navSubmissions,
    navFiles,
    navAdminPermissions,
    navAdminForms,
    navAdminJsonFeeds,
    navAdminUsers,
    navAdminGroups,
    navAdminEmail,
    navAdminEmailLogs,
    navAdminEmailProvider,
    navAdminEmailTest,
    navAdminMongo,
    navAdminSystem,
    navAdminConnectors,
    navCollectionLimits,
    navAdminTenants,
    navAdminActions,
    navActions,
    navInbox,
    hideInbox,
    hideActions,
    hideSubmissions,
    inboxCount,
    navigate: leaveItemEditorAndNavigate,
    onNav,
    handleRetryCollections,
    handleReloadData,
    onCollectionMenuNavigate,
    isCollectionNavListButNoView,
  }
}
